import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Star, Facebook, Globe } from "lucide-react";
import { JoinLoyaltyPrompt } from "@/components/JoinLoyaltyPrompt";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || params.get("token");
  const [state, setState] = useState("checking");
  const [customer, setCustomer] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (data.kind) setKind(data.kind);
          if (data.customer_email) {
            setCustomer({ email: data.customer_email, name: data.customer_name });
          }
          if (data.booking_id) setBookingId(data.booking_id);
          setState("paid");
          return;
        }
      } catch (e) {}
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-6 pt-40 pb-24 text-left">
      {state === "checking" && (
        <div className="flex items-center gap-3 text-muted-foreground font-ui">
          <Loader2 size={22} className="animate-spin gold-text" />
          <span data-testid="payment-verifying-message">Verifying your payment…</span>
        </div>
      )}
      {state === "paid" && (
        <div>
          <CheckCircle2 size={44} className="gold-text" />
          <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight">
            {kind === "card_hold" ? <>Card <span className="italic gold-text">saved</span></> : <>Payment <span className="italic gold-text">received</span></>}
          </h1>
          <p data-testid="payment-confirmed-message" className="mt-4 text-muted-foreground leading-relaxed">
            {kind === "card_hold"
              ? "Thank you — your card is securely on file. It will only be used for post-rental charges per your rental agreement (tolls, fuel, late return, damage)."
              : "Thank you — your payment was received. We're sending your booking information and rental contract to your email right now. We'll see you in Nassau!"}
          </p>
          <div className="mt-8 flex gap-4">
            <Link to="/account">
              <Button data-testid="payment-view-bookings-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
                View My Bookings
              </Button>
            </Link>
            <Link to="/fleet">
              <Button variant="outline" className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
                Book Another
              </Button>
            </Link>
          </div>
          <div data-testid="review-prompt" className="mt-8 border border-border rounded-2xl p-6 bg-card">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Loving the experience so far?</p>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">A quick review means the world to our small local business — pick your favorite spot:</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a data-testid="review-google-link" href="https://www.google.com/maps/search/Easy+Drive+Car+Rental+Nassau+Bahamas" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-transparent px-5 py-2.5 text-sm font-ui hover:bg-black/5 hover:text-primary transition-colors">
                <Star size={15} className="gold-text" /> Google
              </a>
              <a data-testid="review-facebook-link" href="https://www.facebook.com/easydrivecarrental" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-transparent px-5 py-2.5 text-sm font-ui hover:bg-black/5 hover:text-[#1877F2] transition-colors">
                <Facebook size={15} className="text-[#1877F2]" /> Facebook
              </a>
              {bookingId && (
                <Link data-testid="review-webpage-link" to={`/review/${bookingId}`} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-ui text-primary-foreground hover:bg-primary/90 transition-colors">
                  <Globe size={15} /> Our website
                </Link>
              )}
            </div>
          </div>
          {customer && kind !== "card_hold" && <JoinLoyaltyPrompt email={customer.email} name={customer.name} />}
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div>
          <h1 className="font-display text-4xl tracking-tight">Almost there…</h1>
          <p data-testid="payment-pending-message" className="mt-4 text-muted-foreground leading-relaxed">
            We're still confirming your payment with the bank. Check "My Bookings" in a
            few minutes, or contact us at bookings@easydrive242.com if it takes longer.
          </p>
          <Link to="/account" className="inline-block mt-8">
            <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">My Bookings</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
